
import { useEffect, useState, useMemo, use } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import {
  Box,
  Button,
  Icon,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { toastError } from "../../shared/services/errors/erros-handler";
import useLoadingStore from "../../shared/store/loading.store";
import { useNavigate } from "react-router-dom";
import { MovimentacaoObraFormContract } from "./contracts/gerar-movimentacao-obra.contract";
import {
  modalidade,
  MovementTypeEnum,
  movimentacaoObraSchema,
} from "./schema/gerar-movimentacao-obra.schema";
import InputData from "../../shared/components/formulario/input-data/inputData";
import { InputFileUpload } from "../../shared/components/formulario/input-file/inputFile";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import { SelectFuncionarios } from "../../shared/components/formulario/input-select-funcionarios/selectFuncionarios";
import { financeiroService } from "./service/financeiro.service";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import { funcionarioService } from "../colaborador/service/funcionario.service";
import { ModalityEnum, MovementOriginEnum } from "../../shared/utils/enum/enum";
import InputSetores from "../../shared/components/formulario/inputSetores/inputSetor";
import { useSectorById } from "../../shared/components/formulario/inputSetores/use-sector";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import InputPlanoDeContas from "../../shared/components/formulario/inputPlanoDeContas/inputPlanoDeContas";
import { QRCodeCanvas } from "qrcode.react";
import {
  desmascararMoeda,
  gerarPayloadPix,
} from "../../shared/utils/pix.utils";
import { PixKeyTypeEnum } from "../enum";
import { SelectBancosEmpresa } from "../../shared/components/formulario/input-select-bancos-empres/selectBancoEmpresa";
import { SelectAgenciaEmpresa } from "../../shared/components/formulario/input-select-agencia/selectAgencias";
import { SelectContaEmpresa } from "../../shared/components/formulario/input-select-conta-bancaria/selectConta";
import { SelectNfsEmpresa } from "../../shared/components/formulario/input-select-nfs/selectNfs";
import { SelectNfsCompras } from "../../shared/components/formulario/input-select-nfs-compra/selectNfsCompra";
import { useLoader } from "../../shared/contexts/LoaderContext";
import { SelectUnitOfMeasure, UnitOfMeasureEnum } from "../../shared/components/formulario/select/SelectUnitOfMeasure";
import { MovimentacoesAdicionadas } from "./components/movimentacoes-adicionadas";
import { InputSwitch } from "../../shared/components/formulario/input-switch/switch";

type PlanoDeContas = { id: string; code: string; description: string };

export type MovimentacaoObraFormWithNames = MovimentacaoObraFormContract & {
  clientName?: string;
  workName?: string;
  employeeName?: string;
  custoFuncionario?: number;
  isRelatedToNfs?: boolean;
  idNfs?: string;
  nfsNumber?: string;
};

const tiposMovimentacao: Record<string, string> = {
  income: "Entrada",
  outcome: "Saída",
};

export function GerarMovimentacaoFinanceira() {
  const [movimentacoes, setMovimentacoes] = useState<
    MovimentacaoObraFormWithNames[]
  >([]);
  const navigate = useNavigate();
  const { setLoading } = useLoadingStore();
  const [colaborador, setColaborador] = useState<any>(null);
  const [planos, setPlanos] = useState<PlanoDeContas[]>([]);
  const [isPagamentoColaboradorValue, setIsPagamentoColaboradorValue] = useState(false);
  const [payloadPix, setPayloadPix] = useState<string>("");
  const [chavePixColaborador, setChavePixColaborador] = useState<string>("");
  const [isMovimentToBankValue, setIsMovimentToBankValue] = useState(true);
  const [isRelatedToNfsValue, setIsRelatedToNfsValue] = useState(false);
  const [selectedNfs, setSelectedNfs] = useState<any>(null);
  const [selectedNfsCompra, setSelectedNfsCompra] = useState<any>(null);
  const { showLoader, hideLoader } = useLoader();

  const methods = useForm<MovimentacaoObraFormWithNames>({
    resolver: yupResolver(movimentacaoObraSchema),
    defaultValues: {
      value: 0,
      movementType: MovementTypeEnum.OUTCOME,
      isPagamentoColaborador: false,
      isMovimentToBank: true,
      origin: undefined,
      unit: UnitOfMeasureEnum.DAY,
      isRelatedToNfs: false,
      idNfs: "",
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
    watch,
  } = methods;

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: MovimentacaoObraFormWithNames[]) => {
      setLoading(true, "Gerando movimentações...");
      await financeiroService.gerarMovimentacoesObras(data);
    },
    onSuccess: () => {
      toast.success("Movimentações geradas com sucesso!");
      setMovimentacoes([]);
      hideLoader();
      navigate("/financeiro/caixa");
    },
    onError: (err: any) => {
      toastError(err);
    },
    onMutate: () => {
      showLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const idCliente = watch("clientId");
  const employeeId = watch("employeeId");
  const idObra = watch("workId");
  const sector = watch("idSector");
  const quantityToPay = watch("quantityToPay");
  const valorMovimentacao = watch("value");
  const setorSelecionado = useSectorById(sector);
  const tiposMovimentacaoSelecionado = watch("movementType");
  const idBank = watch("idBank");
  const idNfs = watch("idNfs");
  const idNfsCompra = watch("IdInvoicePurchase");
  const isMovimentToBank = watch("isMovimentToBank");
  const isRelatedToNfs = watch("isRelatedToNfs");
  const isPagamentoColaborador = watch("isPagamentoColaborador");

  // Calcula custo do funcionário usando useMemo para reatividade
  const custoFuncionario = useMemo(() => {
    if (
      isPagamentoColaborador &&
      colaborador &&
      colaborador.position &&
      colaborador.position.prices &&
      quantityToPay
    ) {
      const priceObj = colaborador.position.prices.find((price: any) =>
        price.modality.includes(colaborador.modality)
      );
      if (priceObj) {
        const custo = priceObj.value * (quantityToPay ?? 1);
        setValue("value", custo);
        return custo;
      }
    }
    return undefined;
  }, [isPagamentoColaborador, colaborador, quantityToPay, setValue]);


  // Atualiza origin no form quando setorSelecionado mudar
  useEffect(() => {
    if (setorSelecionado?.origin) {
      setValue("origin", setorSelecionado.origin as any);
    }
  }, [setorSelecionado, setValue]);

  // Atualiza isPagamentoColaborador no form quando radio mudar
  useEffect(() => {
    setValue("isPagamentoColaborador", isPagamentoColaborador);
    if (!isPagamentoColaborador) {
      setPayloadPix(""); // Limpa o QR Code se não for pagamento de colaborador
      setColaborador(null);
      setValue("employeeId", undefined);
    }
  }, [isPagamentoColaborador, setValue]);

  // Atualiza isRelatedToNfs no form quando radio mudar
  useEffect(() => {
    setValue("isRelatedToNfs", isRelatedToNfs);
    if (!isRelatedToNfs) {
      setValue("idNfs", "");
      setValue("IdInvoicePurchase", "");
      setSelectedNfs(null);
      setSelectedNfsCompra(null);
    }
  }, [isRelatedToNfs, setValue]);

  // Busca configurações do funcionário quando employeeId muda
  useEffect(() => {
    if (employeeId && isPagamentoColaborador) {
      const fetchJobSettings = async () => {
        try {
          const result = await funcionarioService.configuracoes(employeeId);
          setColaborador(result);
        } catch (error) {
          console.error("Erro ao buscar configurações do funcionário:", error);
          setColaborador(null);
        }
      };
      fetchJobSettings();
    } else {
      setColaborador(null);
    }
  }, [employeeId, isPagamentoColaborador]);

  // Gera o Payload do PIX quando os dados necessários estiverem disponíveis
  useEffect(() => {
    const gerarPayload = () => {
      const chavePixOriginal = colaborador?.pixKey;
      const pixKeyType = colaborador?.pixKeyType;
      const nome = colaborador?.name || colaborador?.fullName;
      const cidade = colaborador?.address?.city || "SAO PAULO";

      let chavePixParaPayload;

      if (chavePixOriginal) {
        if (
          pixKeyType === PixKeyTypeEnum.CPF ||
          pixKeyType === PixKeyTypeEnum.CNPJ ||
          pixKeyType === PixKeyTypeEnum.PHONE
        ) {
          chavePixParaPayload = chavePixOriginal.replace(/\D/g, "");
        } else {
          chavePixParaPayload = chavePixOriginal;
        }
      }

      const valorNumericoDoForm = desmascararMoeda(valorMovimentacao);
      const valor = valorNumericoDoForm || custoFuncionario || 0;

      if (isPagamentoColaborador && chavePixParaPayload && nome && valor > 0) {
        setChavePixColaborador(chavePixOriginal || "");
        const payload = gerarPayloadPix(
          chavePixParaPayload,
          nome,
          cidade,
          valor
        );
        setPayloadPix(payload);
      } else {
        setPayloadPix("");
        setChavePixColaborador("");
      }
    };

    if (colaborador) {
      gerarPayload();
    } else {
      setPayloadPix("");
      setChavePixColaborador("");
    }
  }, [
    colaborador,
    custoFuncionario,
    valorMovimentacao,
    isPagamentoColaborador,
  ]);

  useEffect(() => {
    if (idNfs && isRelatedToNfs) {
      // Aqui você pode buscar detalhes da NFS se necessário
      setSelectedNfs({ id: idNfs });
      setSelectedNfsCompra({ id: idNfsCompra });
    } else {
      setSelectedNfs(null);
      setSelectedNfsCompra(null);
    }
  }, [idNfs, idNfsCompra, isRelatedToNfs]);

  useEffect(() => {
    if (colaborador?.modality) {
      setValue("unit", colaborador.modality);
    } else if (
      setorSelecionado?.origin === MovementOriginEnum.WORK &&
      !employeeId
    ) {
      setValue("unit", UnitOfMeasureEnum.SQUARE_METER);
    }
  }, [colaborador, setorSelecionado, employeeId, setValue]);

  useEffect(() => {
    setIsMovimentToBankValue(isMovimentToBank);
  }, [isMovimentToBank, setValue]);

  useEffect(() => {
    setIsRelatedToNfsValue(isRelatedToNfs || false);
  }, [isRelatedToNfs, setValue]);

  useEffect(() => {
    setIsPagamentoColaboradorValue(isPagamentoColaborador || false);
  }, [isPagamentoColaborador, setValue]);


  const onAddMovimentacao = async (data: MovimentacaoObraFormWithNames) => {

    // Só bloqueia se ambos os clientId existirem e forem diferentes
    if (
      movimentacoes.length > 0 &&
      movimentacoes[0].clientId &&
      data.clientId &&
      movimentacoes[0].clientId !== data.clientId
    ) {
      toast.warning(
        "Não é possível adicionar movimentações de clientes diferentes na mesma listagem."
      );
      return;
    }

    let nfsNumber = "";
    if (isRelatedToNfs && selectedNfs) {
      nfsNumber = selectedNfs.nfsNumber || "";
    }

    setMovimentacoes((prev) => [
      ...prev,
      { ...data, custoFuncionario, isRelatedToNfs, nfsNumber },
    ]);
    // Não limpe estados essenciais!
    reset({
      ...data,
      value: 0,
      movementType: MovementTypeEnum.OUTCOME,
      quantityToPay: 0,
      isPagamentoColaborador: false,
      unit: UnitOfMeasureEnum.DAY,
      isRelatedToNfs: false,
      idNfs: "",
      // mantenha clientId, workId, etc, se necessário
    });
  };

  const onRemoveMovimentacao = (index: number) => {
    setMovimentacoes((prev) => prev.filter((_, i) => i !== index));
  };

  const getLabelQuantidade = (): string => {
    if (colaborador?.modality) {
      switch (colaborador.modality) {
        case ModalityEnum.DAILY:
          return "Informe a quantidade de diárias";
        case ModalityEnum.SQUARE_METERS:
          return "Informe a quantidade de m²";
        case ModalityEnum.TIME:
          return "Informe a quantidade de horas";
        default:
          return "Informe a quantidade";
      }
    }
    if (setorSelecionado?.origin === MovementOriginEnum.WORK && !employeeId) {
      return "Informe a quantidade de horas ou m²";
    }
    return "Informe a quantidade";
  };

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down(1100));

  console.log("Erros do formulário:", errors);

  return (
    <BaseLayout title="Gerar Movimentação">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        mostraBotaoSalvar={false}
        aoClicarBotaoVoltar={() => navigate("/financeiro/caixa")}
      />

      <Grid container spacing={2} sx={{ p: 2, height: "calc(100% - 120px)" }}>
        <Grid item xs={12} md={payloadPix ? 8 : 12}>
          <Box
            component="form"
            onSubmit={handleSubmit(onAddMovimentacao)}
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{ maxWidth: "100%", margin: "0 auto" }}
          >
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                md: mdDown && payloadPix ? "1fr" : "1fr 1fr",
              }}
              gap="20px"
              paddingTop="20px"
            >
              <InputSetores
                name="idSector"
                label="Selecione uma operação"
                methods={methods}
              />
              <Controller
                name="movementType"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Tipo de Movimentação"
                    error={!!errors.movementType}
                    helperText={errors.movementType?.message}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value={MovementTypeEnum.INCOME}>Entrada</MenuItem>
                    <MenuItem value={MovementTypeEnum.OUTCOME}>Saída</MenuItem>
                  </TextField>
                )}
              />
              <InputPlanoDeContas
                name="idChartOfAccounts"
                label="Selecione um plano de contas"
                tiPosMovimentacao={tiposMovimentacaoSelecionado || ""}
                methods={methods}
                planoSelecionado={setPlanos}
              />
              <InputSwitch
                name="isPagamentoColaborador"
                label="Esta movimentação está relacionada a um colaborador?"
                methods={methods}
              />

              <InputSwitch
                name="isMovimentToBank"
                label="Esta movimentação está relacionada a um banco?"
                methods={methods}
              />
              {setorSelecionado?.origin === MovementOriginEnum.WORK && (
                <>
                  <Controller
                    name="clientId"
                    control={control}
                    render={({ field }) => (
                      <SelectCliente
                        value={field.value || ""}
                        onChange={(id, name) => {
                          field.onChange(id);
                          setValue("clientName", name);
                        }}
                        required={
                          setorSelecionado?.origin === MovementOriginEnum.WORK
                        }
                      />
                    )}
                  />
                  <Controller
                    name="workId"
                    control={control}
                    render={({ field }) => (
                      <SelectObra
                        value={field.value || ""}
                        onChange={(id, name) => {
                          field.onChange(id);
                          setValue("workName", name);
                        }}
                        idCliente={idCliente || ""}
                        required={
                          setorSelecionado?.origin === MovementOriginEnum.WORK
                        }
                      />
                    )}
                  />
                </>
              )}
              <InputSwitch
                name="isRelatedToNfs"
                label="Esta movimentação está relacionada a uma entrada de nota fiscal?"
                methods={methods}
              />
              {isRelatedToNfsValue && (
                <>
                  <Controller
                    name="idNfs"
                    control={control}
                    render={({ field }) => (
                      <SelectNfsEmpresa
                        name="idNfs"
                        methods={methods}
                        idWork={idObra || ""}
                        label="Selecione uma Nota Fiscal"
                        disabled={idNfsCompra ? true : false}
                      />
                    )}
                  />

                  <Controller
                    name="IdInvoicePurchase"
                    control={control}
                    render={({ field }) => (
                      <SelectNfsCompras
                        name="IdInvoicePurchase"
                        methods={methods}
                        workId={idObra || ""}
                        label="Selecione uma Nota Fiscal de Compra"
                        disabled={idNfs ? true : false}
                      />
                    )}
                  />
                </>
              )}
              {isPagamentoColaboradorValue && setorSelecionado && (
                <Controller
                  name="employeeId"
                  control={control}
                  render={({ field }) => (
                    <SelectFuncionarios
                      value={field.value || ""}
                      onChange={(id, name) => {
                        field.onChange(id);
                        setValue("employeeName", name);
                      }}
                      idObra={idObra || ""}
                      idSector={setorSelecionado?.id || ""}
                      required={isPagamentoColaborador}
                      disabled={!setorSelecionado?.id}
                    />
                  )}
                />
              )}
              <Box display="flex" gap={2}>
                <Controller
                  name="quantityToPay"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={getLabelQuantidade()}
                      type="number"
                      error={!!errors.quantityToPay}
                      helperText={errors.quantityToPay?.message}
                      size="small"
                      fullWidth
                    />
                  )}
                />
                <SelectUnitOfMeasure name={`unit`} control={control} />
              </Box>
              {isMovimentToBankValue && (
                <Box
                  display="grid"
                  gap={2}
                  gridTemplateColumns={{
                    xs: "1fr",
                    md: mdDown && payloadPix ? "1fr" : "1fr 1fr 1fr",
                  }}
                >
                  <SelectBancosEmpresa
                    name="idBank"
                    methods={methods}
                    label="Selecione um banco"
                    required
                  />
                  <SelectAgenciaEmpresa
                    name="idAgency"
                    methods={methods}
                    idBank={idBank}
                    required
                  />
                  <SelectContaEmpresa
                    name="idAccount"
                    methods={methods}
                    idAgency={methods.watch("idAgency")}
                    required
                  />
                </Box>
              )}
              <InputMascarado
                name="value"
                methods={methods}
                label="Valor R$"
                mask={mascaraMoedaReal}
              />
              <InputData
                name="dateMovement"
                methods={methods}
                label="Data da Movimentação"
              />
              <InputFileUpload
                name="receiptPdf"
                label="Adicione um comprovante nos seguintes formatos (PDF, JPG ou PNG)"
                methods={methods}
              />
            </Box>
            <InputDescricao
              name="description"
              methods={methods}
              label="Descrição"
            />
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="small"
              >
                Adicionar Movimentação
              </Button>
            </Box>
          </Box>
        </Grid>

        {payloadPix && (
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h6">Pagar com PIX</Typography>
              <QRCodeCanvas value={payloadPix} size={200} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Aponte a câmera para pagar
              </Typography>
              <Typography
                variant="caption"
                sx={{ wordBreak: "break-all", textAlign: "center" }}
              >
                <strong>Chave:</strong> {chavePixColaborador}
              </Typography>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} sx={{ maxHeight: "100%", overflowY: "auto", paddingTop: 20 }}>
          <MovimentacoesAdicionadas
            movimentacoes={movimentacoes}
            planos={planos}
            tiposMovimentacao={tiposMovimentacao}
            modalidade={modalidade}
            mutate={mutate}
            isPending={isPending}
            onRemoveMovimentacao={onRemoveMovimentacao}
          />
        </Grid>

      </Grid>

      {/* Lista das movimentações adicionadas */}

    </BaseLayout>
  );
}
